import React, {
  CSSProperties,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

interface IntersectionObserverOptions extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
}

function useIntersectionObserver<T extends HTMLElement = HTMLDivElement>(
  options: IntersectionObserverOptions = {},
): [RefObject<T>, boolean] {
  const {
    threshold = 0.1,
    root = null,
    rootMargin = '0px',
    freezeOnceVisible = true,
  } = options;

  const elementRef = useRef<T>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    const node = elementRef.current;

    // Display content directly without IntersectionObserver support
    if (typeof window === 'undefined' || !window.IntersectionObserver) {
      setIsVisible(true);
      return undefined;
    }

    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);

        if (entry.isIntersecting && freezeOnceVisible) {
          observer.unobserve(node);
        }
      },
      { threshold, root, rootMargin },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [threshold, root, rootMargin, freezeOnceVisible]);

  return [elementRef, isVisible];
}

/**
 * Reveal: scroll effect that animates content in when it becomes visible.
 * Default: 'zoom'. Everything can be overridden per usage via props.
 *
 * ── Examples for other variants ──────────────────────────────────
 *
 * Fade only:
 *   <Reveal variant='fade'>…</Reveal>
 *
 * Slide in from below (subtle / pronounced):
 *   <Reveal variant='fade-up' distance={12} duration={400}>…</Reveal>
 *   <Reveal variant='fade-up' distance={80} duration={1000}>…</Reveal>
 *
 * Slide in from above:
 *   <Reveal variant='fade-down'>…</Reveal>
 *
 * Slide in from the left:
 *   <Reveal variant='fade-right'>…</Reveal>
 *
 * Stronger / subtler zoom, optionally with blur:
 *   <Reveal variant='zoom' scale={0.8}>…</Reveal>
 *   <Reveal variant='zoom' scale={0.98}>…</Reveal>
 *   <Reveal variant='zoom' scale={0.85} blur={6}>…</Reveal>
 *
 * Staggered in a list/grid:
 *   <Reveal delay={(idx % 4) * 80}>…</Reveal>
 *
 * Replay on every scroll in/out:
 *   <Reveal once={false}>…</Reveal>
 *
 * Trigger later (line 100px above the viewport edge):
 *   <Reveal rootMargin='0px 0px -100px 0px'>…</Reveal>
 *
 * Easing alternatives:
 *   easing='ease-out'
 *   easing='cubic-bezier(0.34, 1.56, 0.64, 1)'   // slight overshoot
 * ─────────────────────────────────────────────────────────────────
 */

type RevealVariant = 'fade' | 'fade-up' | 'fade-down' | 'fade-right' | 'zoom';

const getHiddenTransform = (
  variant: RevealVariant,
  distance: number,
  scale: number,
): string => {
  switch (variant) {
    case 'fade-up':
      return `translate3d(0, ${distance}px, 0)`;
    case 'fade-down':
      return `translate3d(0, -${distance}px, 0)`;
    case 'fade-right':
      return `translate3d(-${distance}px, 0, 0)`;
    case 'zoom':
      return `scale(${scale})`;
    default:
      return 'none';
  }
};

const getFilter = (blur: number, isVisible: boolean): string | undefined => {
  if (blur <= 0) return undefined;
  return isVisible ? 'blur(0)' : `blur(${blur}px)`;
};

interface RevealProps {
  children: ReactNode;
  /** Type of effect (default: 'zoom') */
  variant?: RevealVariant;
  /** Delay in ms */
  delay?: number;
  /** Duration in ms */
  duration?: number;
  /** Travel distance in px for fade-up/down/right */
  distance?: number;
  /** Start scale for zoom (0.8 = pronounced, 0.98 = subtle) */
  scale?: number;
  /** CSS easing */
  easing?: string;
  /** Initial blur in px, 0 = off */
  blur?: number;
  threshold?: number;
  rootMargin?: string;
  /** false = animation replays every time the element enters/leaves the viewport */
  once?: boolean;
  className?: string;
}

const DEFAULTS = {
  variant: 'zoom' as RevealVariant,
  delay: 0,
  duration: 900,
  distance: 16,
  scale: 0.98,
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  blur: 0,
  threshold: 0.15,
  rootMargin: '0px 0px -60px 0px',
  once: false,
  className: undefined as string | undefined,
};

// Respect the OS-level "reduce motion" setting (e.g. Windows "Animation effects" off)
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const Reveal = ({
  children,
  variant = DEFAULTS.variant,
  delay = DEFAULTS.delay,
  duration = DEFAULTS.duration,
  distance = DEFAULTS.distance,
  scale = DEFAULTS.scale,
  easing = DEFAULTS.easing,
  blur = DEFAULTS.blur,
  threshold = DEFAULTS.threshold,
  rootMargin = DEFAULTS.rootMargin,
  once = DEFAULTS.once,
  className = DEFAULTS.className,
}: RevealProps) => {
  const [ref, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold,
    rootMargin,
    freezeOnceVisible: once,
  });

  // Render content immediately without animation if reduced motion is requested
  if (prefersReducedMotion()) {
    return <div className={className}>{children}</div>;
  }

  const style: CSSProperties = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible
      ? 'none'
      : getHiddenTransform(variant, distance, scale),
    filter: getFilter(blur, isVisible),
    transition: ['opacity', 'transform', 'filter']
      .map(prop => `${prop} ${duration}ms ${easing} ${delay}ms`)
      .join(', '),
  };

  return (
    <div ref={ref} style={style} className={className}>
      {children}
    </div>
  );
};

Reveal.defaultProps = DEFAULTS;

export default Reveal;
